import React, { useState, useEffect } from 'react';
import {
  Box,
  Dialog,
  DialogTitle,
  DialogContent,
  TextField,
  Typography,
  Chip,
  Grid,
  MenuItem,
  IconButton,
} from '@mui/material';
import {
  Add as AddIcon,
  Close as CloseIcon,
} from '@mui/icons-material';
import { motion } from 'framer-motion';
import { API } from '../services/api';
import { showSuccess, showError } from '../utils/notifications';
import DataTable from '../components/DataTable';
import FilterBar from '../components/FilterBar';
import ConfirmDialog from '../components/ConfirmDialog';
import ActionButton from '../components/ActionButton';
import TableSkeleton from '../components/TableSkeleton';

const MotionBox = motion(Box);

function EquipmentSpecs() {
  const [specs, setSpecs] = useState([]);
  const [equipment, setEquipment] = useState([]);
  const [units, setUnits] = useState([]);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const [confirmDialog, setConfirmDialog] = useState({ open: false, specId: null });
  const [editingSpec, setEditingSpec] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [typeFilter, setTypeFilter] = useState('all');
  const [criticalFilter, setCriticalFilter] = useState('all');
  const [formData, setFormData] = useState({
    equipment_id: '',
    spec_name: '',
    spec_value: '',
    unit_id: '',
    spec_type: '',
    is_critical: false,
    notes: '',
  });

  const fetchData = async () => {
    try {
      setLoading(true);
      const [specsRes, equipmentRes, unitsRes] = await Promise.all([
        API.equipmentSpecs.list(),
        API.equipment.list(),
        API.units.list(),
      ]);
      setSpecs(specsRes?.data || []);
      setEquipment(equipmentRes?.data || []);
      setUnits(unitsRes?.data || []);
    } catch (err) {
      showError('Failed to load equipment specifications');
      console.error('Equipment specs fetch error:', err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, []);

  const handleOpenDialog = (spec = null) => {
    if (spec) {
      setEditingSpec(spec);
      setFormData({
        equipment_id: spec.equipment_id || '',
        spec_name: spec.spec_name || '',
        spec_value: spec.spec_value || '',
        unit_id: spec.unit_id || '',
        spec_type: spec.spec_type || '',
        is_critical: spec.is_critical || false,
        notes: spec.notes || '',
      });
    } else {
      setEditingSpec(null);
      setFormData({
        equipment_id: '',
        spec_name: '',
        spec_value: '',
        unit_id: '',
        spec_type: '',
        is_critical: false,
        notes: '',
      });
    }
    setOpen(true);
  };

  const handleCloseDialog = () => {
    setOpen(false);
    setEditingSpec(null);
    setFormData({
      equipment_id: '',
      spec_name: '',
      spec_value: '',
      unit_id: '',
      spec_type: '',
      is_critical: false,
      notes: '',
    });
  };

  const handleSubmit = async () => {
    try {
      if (editingSpec) {
        await API.equipmentSpecs.update(editingSpec.spec_id, formData);
        showSuccess('Specification updated successfully!');
      } else {
        await API.equipmentSpecs.create(formData);
        showSuccess('Specification created successfully!');
      }
      handleCloseDialog();
      fetchData();
    } catch (err) {
      const errorMessage = err.response?.data?.message || 
        (editingSpec ? 'Failed to update specification' : 'Failed to create specification');
      showError(errorMessage);
      console.error('Equipment spec submit error:', err);
    }
  };

  const handleDeleteClick = (specId) => {
    setConfirmDialog({ open: true, specId });
  };

  const handleDelete = async () => {
    try {
      await API.equipmentSpecs.deactivate(confirmDialog.specId);
      showSuccess('Specification deleted successfully!');
      fetchData();
    } catch (err) {
      showError('Failed to delete specification');
      console.error('Equipment spec delete error:', err);
    }
  };

  const getEquipmentName = (equipmentId) => {
    const eq = equipment.find(e => e.equipment_id === equipmentId);
    return eq ? eq.equipment_name : 'Unknown Equipment';
  };

  const getUnitName = (unitId) => {
    const unit = units.find(u => u.unit_id === unitId);
    return unit ? `${unit.unit_name} (${unit.unit_symbol})` : 'Unknown Unit';
  };

  const uniqueTypes = [...new Set(specs.map(s => s.spec_type).filter(Boolean))];

  const filteredSpecs = specs.filter(spec => {
    const matchesSearch = spec.spec_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      getEquipmentName(spec.equipment_id)?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      spec.spec_type?.toLowerCase().includes(searchTerm.toLowerCase());
    
    const matchesType = typeFilter === 'all' || spec.spec_type === typeFilter;
    const matchesCritical = criticalFilter === 'all' || 
      (criticalFilter === 'critical' && spec.is_critical) ||
      (criticalFilter === 'normal' && !spec.is_critical);
    
    return matchesSearch && matchesType && matchesCritical;
  });

  const columns = [
    {
      field: 'equipment_id',
      headerName: 'Equipment',
      width: '20%',
      render: (row) => (
        <Typography variant="body2" fontWeight={600}>
          {getEquipmentName(row.equipment_id)}
        </Typography>
      ),
    },
    {
      field: 'spec_name',
      headerName: 'Specification Name',
      width: '18%',
    },
    {
      field: 'spec_value',
      headerName: 'Value',
      width: '12%',
      render: (row) => (
        <Typography variant="body2" fontWeight={600}>
          {row.spec_value}
        </Typography>
      ),
    },
    {
      field: 'unit_id',
      headerName: 'Unit',
      width: '15%',
      render: (row) => getUnitName(row.unit_id),
    },
    {
      field: 'spec_type',
      headerName: 'Type',
      width: '15%',
      render: (row) => row.spec_type || '-',
    },
    {
      field: 'is_critical',
      headerName: 'Critical',
      width: '10%',
      render: (row) => (
        <Chip
          label={row.is_critical ? 'Critical' : 'Normal'}
          color={row.is_critical ? 'error' : 'default'}
          size="small"
          sx={{ fontWeight: 600 }}
        />
      ),
    },
    {
      field: 'created_at',
      headerName: 'Created',
      width: '10%',
      render: (row) => row.created_at ? new Date(row.created_at).toLocaleDateString() : '-',
    },
  ];

  const filterFields = [
    {
      label: 'Type',
      value: typeFilter,
      onChange: setTypeFilter,
      options: [
        { value: 'all', label: 'All Types' },
        ...uniqueTypes.map(type => ({ value: type, label: type })),
      ],
    },
    {
      label: 'Critical',
      value: criticalFilter,
      onChange: setCriticalFilter,
      options: [
        { value: 'all', label: 'All' },
        { value: 'critical', label: 'Critical' },
        { value: 'normal', label: 'Normal' },
      ],
    },
  ];

  if (loading) {
    return (
      <Box>
        <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
          <Typography variant="h5" component="h1" sx={{ fontWeight: 700 }}>
            Equipment Specifications
          </Typography>
        </Box>
        <TableSkeleton columns={6} rows={6} />
      </Box>
    );
  }

  return (
    <MotionBox
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      transition={{ duration: 0.3 }}
    >
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
        <Box>
          <Typography variant="h5" component="h1" sx={{ fontWeight: 700, mb: 0.5 }}>
            Equipment Specifications
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Manage technical specifications for equipment
          </Typography>
        </Box>
        <ActionButton
          startIcon={<AddIcon />}
          onClick={() => handleOpenDialog()}
        >
          Add Specification
        </ActionButton>
      </Box>

      <FilterBar
        searchTerm={searchTerm}
        onSearchChange={setSearchTerm}
        searchPlaceholder="Search specifications..."
        filterFields={filterFields}
      />

      <DataTable
        columns={columns}
        data={filteredSpecs}
        onEdit={handleOpenDialog}
        onDelete={(row) => handleDeleteClick(row.spec_id)}
        getRowId={(row) => row.spec_id}
        emptyMessage="No specifications found"
      />

      <Dialog 
        open={open} 
        onClose={handleCloseDialog} 
        maxWidth="md" 
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: 4,
          },
        }}
      >
        <DialogTitle sx={{ 
          pb: 1, 
          display: 'flex', 
          alignItems: 'center', 
          justifyContent: 'space-between',
        }}>
          <Typography variant="h6" fontWeight={700}>
            {editingSpec ? 'Edit Specification' : 'Add New Specification'}
          </Typography>
          <IconButton onClick={handleCloseDialog} size="small">
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent sx={{ pt: 3 }}>
          <Grid container spacing={3}>
            <Grid item xs={12} sm={6}>
              <TextField
                select
                fullWidth
                label="Equipment"
                value={formData.equipment_id}
                onChange={(e) => setFormData({ ...formData, equipment_id: e.target.value })}
                required
                sx={{ '& .MuiInputLabel-root': { backgroundColor: 'white', px: 0.5 } }}
              >
                {equipment.map((eq) => (
                  <MenuItem key={eq.equipment_id} value={eq.equipment_id}>
                    {eq.equipment_name}
                  </MenuItem>
                ))}
              </TextField>
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Specification Name"
                value={formData.spec_name}
                onChange={(e) => setFormData({ ...formData, spec_name: e.target.value })}
                required
                sx={{ '& .MuiInputLabel-root': { backgroundColor: 'white', px: 0.5 } }}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Value"
                type="number"
                value={formData.spec_value}
                onChange={(e) => setFormData({ ...formData, spec_value: e.target.value })}
                required
                sx={{ '& .MuiInputLabel-root': { backgroundColor: 'white', px: 0.5 } }}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                select
                fullWidth
                label="Unit"
                value={formData.unit_id}
                onChange={(e) => setFormData({ ...formData, unit_id: e.target.value })}
                required
                sx={{ '& .MuiInputLabel-root': { backgroundColor: 'white', px: 0.5 } }}
              >
                {units.map((unit) => (
                  <MenuItem key={unit.unit_id} value={unit.unit_id}>
                    {unit.unit_name} ({unit.unit_symbol})
                  </MenuItem>
                ))}
              </TextField>
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Specification Type"
                value={formData.spec_type}
                onChange={(e) => setFormData({ ...formData, spec_type: e.target.value })}
                sx={{ '& .MuiInputLabel-root': { backgroundColor: 'white', px: 0.5 } }}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                select
                fullWidth
                label="Critical"
                value={formData.is_critical}
                onChange={(e) => setFormData({ ...formData, is_critical: e.target.value === 'true' })}
                sx={{ '& .MuiInputLabel-root': { backgroundColor: 'white', px: 0.5 } }}
              >
                <MenuItem value="false">Normal</MenuItem>
                <MenuItem value="true">Critical</MenuItem>
              </TextField>
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Notes"
                value={formData.notes}
                onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                multiline
                rows={2}
                sx={{ '& .MuiInputLabel-root': { backgroundColor: 'white', px: 0.5 } }}
              />
            </Grid>
          </Grid>
        </DialogContent>
        <Box sx={{ px: 3, pb: 3, display: 'flex', gap: 2, justifyContent: 'flex-end' }}>
          <ActionButton variant="outlined" onClick={handleCloseDialog}>
            Cancel
          </ActionButton>
          <ActionButton variant="contained" onClick={handleSubmit}>
            {editingSpec ? 'Update Specification' : 'Create Specification'}
          </ActionButton>
        </Box>
      </Dialog>

      <ConfirmDialog
        open={confirmDialog.open}
        onClose={() => setConfirmDialog({ open: false, specId: null })}
        onConfirm={handleDelete}
        title="Delete Specification"
        message="Are you sure you want to delete this specification? This action cannot be undone."
        confirmText="Delete"
        type="danger"
      />
    </MotionBox>
  );
}

export default EquipmentSpecs;